import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TabStyles } from '../../styles/TabStyles';
import { TextFieldProps, TextFieldWidget } from '../TextField/TextFieldWidget';
import { makeTestId } from '../../utils/Declarative';
import { ColorSwatchWidget } from './ColorGridWidget';

const { RowHeight, BorderWidth } = TabStyles.Sizing;

export interface HexColorInputWidgetState {
  hexColorText: string;
}

export interface HexColorInputProps {
  color: string;
  label: string;
  onConfirm: (color: string) => void;
  testId: string;
}

const RootStyleName = TypeStyle.style({
  alignItems: 'flex-end',
  display: 'flex',
  paddingTop: TabStyles.Spacing.SmallSpace,
});

// we set min height/width so that IE doesn't shrink the width of the box to less than row height
// see Defect 736928 for more details
const PreviewSwatchStyleName = TypeStyle.style({
  borderColor: TabStyles.Colors.L6,
  borderStyle: 'solid',
  borderWidth: BorderWidth,
  boxSizing: 'border-box',
  height: RowHeight,
  marginRight: 12,
  minHeight: RowHeight,
  minWidth: RowHeight,
  width: RowHeight,
});

const TextFieldStyleName = TypeStyle.style({ flexGrow: 1, flexBasis: '50px' });
const InputStyle: React.CSSProperties = { width: '100%' };

function isValidHexColor(color: string): boolean {
  return /^#?[0-9A-Fa-f]{6}$/.test(color);
}

function formatColor(color: string): string {
  let formattedColor = (color || '').toUpperCase();
  if (formattedColor.charAt(0) !== '#') {
    formattedColor = '#' + formattedColor;
  }
  return formattedColor;
}

export class HexColorInput extends React.Component<HexColorInputProps, HexColorInputWidgetState> {
  public state: HexColorInputWidgetState = {
    hexColorText: this.props.color || '',
  };

  public render(): JSX.Element {
    return (
      <div className={RootStyleName} data-tb-test-id={makeTestId(this.props.testId, 'HexInput')}>
        <div className={PreviewSwatchStyleName}>
          <ColorSwatchWidget
            color={this.getCustomColor() || 'transparent'}
            disabled={!this.getCustomColor()}
            onClick={this.handleSwatchClicked}
            selected={this.shouldSwatchBeSelected()}
            size={RowHeight - 2 * BorderWidth}
            testId={this.props.testId}
          />
        </div>
        <TextFieldWidget
          className={TextFieldStyleName}
          label={this.props.label}
          maxLength={7}
          onChange={this.handleChange}
          /* onClear is omitted because the TextField from Tableau UI calls stopPropagation on ESC events when there is text present --
           * which is always the case for this control, as it always displays the '#' prefix. Removing this stopPropagation would be
           * technically-correct but would break dialogs/overlays, etc. Easiest way to work around is just not to show the clear button. */
          onKeyUp={this.handleKeyUp}
          placeholder='#'
          // TFSID 1165428: Default is 20, which causes the input to grow too wide.
          size={10}
          spellCheck={false}
          style={InputStyle}
          testId={this.props.testId}
          valid={this.isValid()}
          value={formatColor(this.state.hexColorText)}
        />
      </div>
    );
  }

  private isValid = (): TextFieldProps['valid'] => {
    if (!this.state.hexColorText || this.state.hexColorText === '#' || isValidHexColor(this.state.hexColorText)) {
      return undefined;
    }
    return false;
  }

  private handleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const { value } = e.target;
    if (!/^#?[0-9A-Fa-f]{0,6}$/.test(value)) { return; }
    this.setState({ hexColorText: e.target.value });
  }

  private handleKeyUp: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (isValidHexColor(this.state.hexColorText)) {
        this.props.onConfirm(formatColor(this.state.hexColorText));
      }
    }
  }

  private handleSwatchClicked = (color: string): void => {
    if (isValidHexColor(this.state.hexColorText)) {
      this.props.onConfirm(formatColor(color.toLowerCase()));
    }
  }

  private getCustomColor(): string | null {
    if (isValidHexColor(this.state.hexColorText)) {
      return formatColor(this.state.hexColorText);
    }
    return null;
  }

  private shouldSwatchBeSelected(): boolean {
    return isValidHexColor(this.props.color) && formatColor(this.state.hexColorText) === formatColor(this.props.color);
  }
}
